import * as THREE from 'three';

export type ForgedVehicleSurface = {
  bump: THREE.CanvasTexture;
  roughness: THREE.CanvasTexture;
  dispose: () => void;
};

function randomFactory(initialSeed: number): () => number {
  let seed = initialSeed >>> 0;
  return () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
}

function textureFromCanvas(
  name: string,
  canvas: HTMLCanvasElement,
  repeat: number,
): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.name = name;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.colorSpace = THREE.NoColorSpace;
  texture.flipY = false;
  texture.anisotropy = 8;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  return texture;
}

export function createForgedVehicleSurface(
  seed = 0x7a41f7,
  size = 512,
): ForgedVehicleSurface {
  const bumpCanvas = document.createElement('canvas');
  const roughnessCanvas = document.createElement('canvas');
  bumpCanvas.width = bumpCanvas.height = size;
  roughnessCanvas.width = roughnessCanvas.height = size;
  const bump = bumpCanvas.getContext('2d');
  const roughness = roughnessCanvas.getContext('2d');
  if (!bump || !roughness) throw new Error('Canvas 2D unavailable for vehicle surface forge.');

  const random = randomFactory(seed);
  bump.fillStyle = '#7e7e7e';
  bump.fillRect(0, 0, size, size);
  roughness.fillStyle = '#7a7a7a';
  roughness.fillRect(0, 0, size, size);

  const cell = size / 8;
  bump.strokeStyle = 'rgba(35,35,35,0.72)';
  roughness.strokeStyle = 'rgba(205,205,205,0.52)';
  bump.lineWidth = 3;
  roughness.lineWidth = 5;
  for (let index = 0; index <= 8; index += 1) {
    const position = index * cell + (index % 2) * 5;
    bump.beginPath();
    bump.moveTo(position, 0);
    bump.lineTo(position, size);
    bump.stroke();
    roughness.beginPath();
    roughness.moveTo(position, 0);
    roughness.lineTo(position, size);
    roughness.stroke();
  }
  for (let index = 0; index <= 8; index += 1) {
    const position = index * cell;
    bump.beginPath();
    bump.moveTo(0, position);
    bump.lineTo(size, position);
    bump.stroke();
    roughness.beginPath();
    roughness.moveTo(0, position);
    roughness.lineTo(size, position);
    roughness.stroke();
  }

  for (let index = 0; index < 1500; index += 1) {
    const x = random() * size;
    const y = random() * size;
    const value = 72 + Math.floor(random() * 100);
    const alpha = 0.08 + random() * 0.24;
    bump.fillStyle = `rgba(${value},${value},${value},${alpha})`;
    bump.fillRect(x, y, 0.6 + random() * 2, 0.5 + random());
    const roughValue = 86 + Math.floor(random() * 94);
    roughness.fillStyle = `rgba(${roughValue},${roughValue},${roughValue},${0.08 + random() * 0.18})`;
    roughness.fillRect(x, y, 1 + random() * 3, 0.6 + random() * 1.4);
  }

  bump.strokeStyle = 'rgba(225,225,215,0.5)';
  bump.lineWidth = 1;
  for (let index = 0; index < 72; index += 1) {
    const x = random() * size;
    const y = random() * size;
    bump.beginPath();
    bump.moveTo(x, y);
    bump.lineTo(x + 6 + random() * 38, y - 2 + random() * 4);
    bump.stroke();
  }

  const bumpTexture = textureFromCanvas('TH01_ForgedMicroSurface', bumpCanvas, 2.5);
  const roughnessTexture = textureFromCanvas(
    'TH01_ForgedRoughness',
    roughnessCanvas,
    2.5,
  );
  return {
    bump: bumpTexture,
    roughness: roughnessTexture,
    dispose: () => {
      bumpTexture.dispose();
      roughnessTexture.dispose();
    },
  };
}
